import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Overline } from "@/components/Section";
import Markdown from "@/components/Markdown";
import { ConfidentialName } from "@/components/PlatformCard";

export default function PlatformDetail() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [live, setLive] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    api.get(`/public/platforms/${slug}.json`).then((r) => {
      if (r.data.error) { setState("notfound"); return; }
      setCard(r.data);
      setState("ready");
    }).catch(() => setState("notfound"));

    if (slug === "ideadepot") {
      api.get("/public/ideadepot-live").then((r) => setLive(r.data.card)).catch(() => {});
    }
  }, [slug]);

  if (state === "loading") {
    return <div className="section-pad container-tib flex justify-center"><Loader2 className="w-8 h-8 text-tib-amber animate-spin" /></div>;
  }
  if (state === "notfound") {
    return (
      <div className="section-pad container-tib text-center" data-testid="platform-notfound">
        <h1 className="h-serif text-4xl text-white">Platform not found.</h1>
        <Link to="/platforms" className="btn-secondary mt-8 inline-flex">Back to portfolio</Link>
      </div>
    );
  }

  const metrics = (live && live.metrics_snapshot) || card.metrics_snapshot || {};
  const isSoon = card.status === "coming_soon";
  const statusLabel = card.status === "coming_soon" ? "coming soon" : card.status;
  const visitUrl = card.primary_url
    ? `${card.primary_url}?ref=tib&utm_source=tib&utm_medium=platform-card`
    : "";

  return (
    <div data-testid={`platform-detail-${slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-zinc-900">
        <div className="absolute inset-0">
          {card.hero_image_url ? (
            <img src={card.hero_image_url} alt={card.name} className="w-full h-full object-cover opacity-25" />
          ) : (
            <div
              className="w-full h-full"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 25% 30%, rgba(47,107,255,0.18), transparent 55%), radial-gradient(circle at 78% 70%, rgba(124,63,228,0.16), transparent 55%), linear-gradient(#0a0a0a,#000)",
              }}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        </div>
        <div className="container-tib relative z-10 pt-16 pb-20">
          <Link to="/platforms" className="text-zinc-400 hover:text-tib-gold inline-flex items-center gap-2 text-sm mb-10">
            <ArrowLeft className="w-4 h-4" /> Portfolio
          </Link>
          <div className="flex items-center gap-3 mb-5">
            <Overline className={card.ownership === "external" ? "!text-tib-royal" : ""}>{card.ownership === "internal" ? "Internal · MPGBF-owned" : (card.confidential ? "External · Confidential" : "External · Client-owned")}</Overline>
            <span className={`text-[0.62rem] font-mono uppercase tracking-widest px-2 py-1 border ${card.confidential ? "text-tib-gold border-tib-gold/40" : (isSoon ? "text-tib-gold border-tib-gold/40" : "text-tib-teal border-tib-teal/40")}`}>{card.confidential ? "confidential" : statusLabel}</span>
          </div>
          <h1 className="h-serif text-5xl md:text-7xl text-white max-w-3xl">
            {card.confidential ? <ConfidentialName name={card.name} /> : card.name}
          </h1>
          <p className="mt-6 text-xl text-zinc-300 max-w-2xl">{card.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-4">
            {isSoon ? (
              <>
                {visitUrl ? (
                  <a href={visitUrl} target="_blank" rel="noopener noreferrer" data-testid="platform-visit" className="btn-secondary">
                    Preview at {card.primary_url.replace(/^https?:\/\//, "")} <ArrowUpRight className="w-4 h-4" />
                  </a>
                ) : null}
                <Link to="/contact" data-testid="platform-notify" className="btn-primary">
                  Get notified at launch <ArrowUpRight className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <a href={visitUrl} target="_blank" rel="noopener noreferrer" data-testid="platform-visit" className="btn-primary">
                Visit {card.name} <ArrowUpRight className="w-4 h-4" />
              </a>
            )}
            <span className="text-xs font-mono text-zinc-600 self-center">Built by {card.built_by}</span>
          </div>
        </div>
      </section>

      {/* LIVE METRIC STRIP */}
      {Object.keys(metrics).length > 0 && (
        <section className="border-b border-zinc-900 bg-tib-ink">
          <div className="container-tib grid grid-cols-2 md:grid-cols-4" data-testid="live-metric-strip">
            {Object.entries(metrics).map(([k, v], i) => (
              <div key={k} className={`py-8 px-4 ${i !== 0 ? "md:border-l border-zinc-900" : ""}`}>
                <div className="h-serif text-3xl md:text-4xl text-tib-amber">{String(v)}</div>
                <div className="overline mt-2 text-zinc-500">{k.replace(/_/g, " ")}</div>
              </div>
            ))}
          </div>
          {live && <p className="container-tib pb-4 text-[0.65rem] font-mono text-zinc-700">● live feed via /api/public/platform-card.json</p>}
        </section>
      )}

      {/* CASE STUDY */}
      <section className="section-pad">
        <div className="container-tib grid lg:grid-cols-[1fr_280px] gap-14">
          <article>
            {card.summary && <p className="text-2xl text-zinc-200 h-serif leading-snug mb-8">{card.summary}</p>}
            <Markdown content={card.case_study_md || ""} />
          </article>
          <aside className="lg:sticky lg:top-24 self-start space-y-6">
            <div className="card-edge p-6">
              <Overline className="mb-4">Details</Overline>
              <dl className="space-y-4 text-sm">
                <div><dt className="text-zinc-600 font-mono text-xs uppercase">Launched</dt><dd className="text-white mt-1">{card.launched_at || "—"}</dd></div>
                <div><dt className="text-zinc-600 font-mono text-xs uppercase">Ownership</dt><dd className="text-white mt-1 capitalize">{card.ownership}</dd></div>
                <div>
                  <dt className="text-zinc-600 font-mono text-xs uppercase">Categories</dt>
                  <dd className="mt-2 flex flex-wrap gap-2">
                    {(card.categories || []).map((c) => (
                      <span key={c} className="text-[0.62rem] font-mono uppercase tracking-widest text-zinc-400 border border-zinc-800 px-2 py-1">{c}</span>
                    ))}
                  </dd>
                </div>
              </dl>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
